import {
  isChapter,
  isExpanded,
  isNews,
  isOrg,
  isQuote,
  isRepost,
  isTitle,
  isUser,
  Post,
  type PostEntity,
} from "~/src/api";
import { useSessionStorage } from "@vueuse/core";
import {
  formatBasePost,
  POST_INCLUDES,
  type CachePost,
  formatOrg,
  formatUser,
} from "~/src/feed/postUtils";
import { useBatchFetcher } from "~/composables/async/useBatchFetcher";

const CACHE_DURATION = 1000 * 60 * 5;
const CACHE_KEY = "cache.post";

const isContentRelation = (rel: PostEntity["relationships"][number]) =>
  isRepost(rel) ||
  isQuote(rel) ||
  isChapter(rel) ||
  isTitle(rel) ||
  isNews(rel);

const postCache = useSessionStorage<{
  [key in string]: PostEntity & { timeout: number };
}>(CACHE_KEY, () => ({}));

const cachePost = (post: PostEntity) => {
  if (process.server) return;
  if (
    post.relationships.find(
      (rel) => !isExpanded(rel) && !isContentRelation(rel),
    )
  ) {
    // throw new Error(
    //   `Post with Id ${post.id} not cached, all relationships except RP, QRP, chapter, and Title must be expanded to cache a post`
    // );
    console.warn(
      `Post with Id ${post.id} not cached, all relationships except RP, QRP, chapter, and Title must be expanded to cache a post`,
    );
    return;
  }

  postCache.value[post.id] = {
    ...post,
    timeout: Date.now(),
  };
};
const getCachedPost = (id: string) => {
  if (process.server) return null;
  const post = postCache.value[id];
  if (!post || Date.now() - post.timeout > CACHE_DURATION) {
    delete postCache.value[id];
    return null;
  }

  return post;
};

export const usePostBatch = () => {
  const nuxtApp = useNuxtApp();
  const authStore = nuxtApp.$auth();

  return useBatchFetcher(
    "post-batcher",
    async (ids) => {
      if (ids.some((id) => !getCachedPost(id))) {
        const token = await authStore?.getToken();
        const res = await Post.getPosts(
          {
            ids,
            includes: POST_INCLUDES,
          },
          token || undefined,
        );

        res.data.forEach(cachePost);
        return res.data;
      }

      return ids.map((id) => getCachedPost(id)!);
    },
    true,
  );
};

//TODO: add session cache for if user has reposted a post
export const usePostSessionCache = () => {
  let batcher = usePostBatch();
  const config = useRuntimeConfig();

  // posts can be no longer than 20 items, expanded
  const getBatch = async (posts: PostEntity[]): Promise<CachePost[]> => {
    if (posts.length === 0) return [];
    posts.forEach(cachePost);

    return await convertToFEPostEntity(posts);
  };

  async function getPost(
    id: string,
    auth: string | undefined = undefined,
    ignoreCache = false,
  ): Promise<CachePost> {
    let cachedPost: PostEntity | null = getCachedPost(id);
    if (ignoreCache || !cachedPost) {
      cachedPost = await Post.getPost(id, POST_INCLUDES, auth);
      cachePost(cachedPost);
    }

    return (await convertToFEPostEntity([cachedPost])).at(0)!;
  }

  const convertToFEPostEntity = async (
    posts: PostEntity[],
  ): Promise<CachePost[]> => {
    const toBeFetched = new Set<string>();
    const requiredPosts = posts;

    // 1 level down, i.e. reposts of posts or qrps of posts
    posts.forEach((post) => {
      const targetPost = post.relationships.find(
        (rel) => isRepost(rel) || isQuote(rel),
      )?.id;
      if (targetPost && !getCachedPost(targetPost)) toBeFetched.add(targetPost);
    });
    const targetPosts = (
      await batcher.value.getByIds(Array.from(toBeFetched))
    ).filter(onlyTruthys);
    requiredPosts.push(...targetPosts);

    // 2 levels down, i.e. reposts of qrp
    toBeFetched.clear();
    targetPosts.forEach((post) => {
      if (post.attributes.repostKind !== "soft_repost") return;
      const targetPost = post.relationships.find(isQuote)!.id;
      if (targetPost && !getCachedPost(targetPost)) toBeFetched.add(targetPost);
    });
    const subsequentPosts = (
      await batcher.value.getByIds(Array.from(toBeFetched))
    ).filter(onlyTruthys);
    requiredPosts.push(...subsequentPosts);

    // we assert that it exists because we fetched it, this addition here makes this block SSR safe
    const getFromFetchedPosts = (id: string) =>
      (requiredPosts.find((post) => post.id === id) || getCachedPost(id))!;

    // synchronous construction of posts, executed with the assumption that all posts are cached or fetched
    return (
      posts
        .map((post) => {
          let topLevelPost = post;
          // hoist content post if parent is a soft repost
          if (post.attributes.repostKind === "soft_repost") {
            const targetId = post.relationships.find((rel) =>
              isRepost(rel),
            )?.id;
            if (targetId) topLevelPost = getFromFetchedPosts(targetId);
            // hide soft reposts that reference a deleted post until BE removes it
            if (!topLevelPost) return null;
          }

          // this is the main post body, the author here is displayed as the author on a post
          let base = formatBasePost(topLevelPost, config.public.baseUrl, false);
          let repostData: CachePost["repostData"] = undefined;

          // handle repost info for the post
          if (
            (topLevelPost.attributes.repostKind &&
              topLevelPost.attributes.repostKind !== "comment_portal") ||
            post.attributes.repostKind === "soft_repost"
          ) {
            const postAuthor = post.relationships.find(isUser);
            const postOrg = post.relationships.find(isOrg);
            const targetId = topLevelPost.relationships.find(
              (rel) => rel.type === topLevelPost.attributes.repostKind,
            )?.id;

            const targetPost = targetId && getFromFetchedPosts(targetId);

            // this is where we add repost data
            repostData = {
              originalPostId: post.id,
              originalPostTimestamp: post.attributes.createdAt,
              // this gets populated if original top level post was a soft repost
              repostedBy:
                post.attributes.repostKind === "soft_repost"
                  ? [
                      postOrg && isExpanded(postOrg)
                        ? formatOrg(postOrg)
                        : formatUser(postAuthor),
                    ]
                  : [],
              // this gets populated if computed top level post is a QRP
              targetPost: targetPost
                ? formatBasePost(targetPost, config.public.baseUrl)
                : undefined,
            };
          }

          return {
            ...base,
            repostData,
          } as CachePost;
        })
        // null post means it was a soft repost whose target could not be found
        .filter(onlyTruthys)
    );
  };

  const clear = () => {
    if (!window) return false;
    window.sessionStorage.clear();
    return true;
  };

  return {
    getBatch,
    getPost,
    clear,
  };
};
